import { useRef, useState } from "react";
import TurnstileField, { turnstileSiteKey } from "./TurnstileField";
import { motion } from "framer-motion";
import { useNavigate, Link } from "react-router-dom";
import { Loader2, Eye, EyeOff, AlertCircle, CheckCircle } from "lucide-react";
import OAuthSocialButtons from "./OAuthSocialButtons";
import { AuthInstrumentShell } from "./AuthInstrumentShell";
import {
  AUTH_PASSWORD_SCROLL_MARGIN_CLASS,
  REGISTER_NAME_FIELD_CLASS,
  REGISTER_NAME_GRID_CLASS,
  REGISTER_NAME_GRID_TESTID,
  REGISTER_NAME_INPUT_CLASS,
} from "../../lib/wwwPhoneClipLayout";
import { trackSignup } from "../../lib/analytics";
import {
  LOGIN_PENDING_VERIFICATION_STATE,
  REGISTER_CHECK_EMAIL_TESTID,
  persistPendingEmailVerification,
} from "../../lib/pendingEmailVerification";
import {
  REGISTER_FIELD_AUTOCOMPLETE,
  applyRegisterProtectedChange,
  createRegisterTypedFieldGuard,
  isRegisterUserBeforeInputType,
  isRegisterUserEditKey,
  syncEmailFromDom,
} from "../../lib/registerAutofill";
import { getAuthHeaders, getWebApiBase, persistWebSession } from "../../lib/webApi";
import i18n, { useTranslation } from "../../lib/i18n";

const API_BASE = getWebApiBase();

function authUnavailableMessage() {
  return i18n.t("auth.errors.unableToReachService");
}

function toSafeAuthMessage(raw, fallback) {
  const resolvedFallback = fallback ?? authUnavailableMessage();
  if (!raw) return resolvedFallback;
  const message = String(raw).trim();
  if (!message) return resolvedFallback;
  const lower = message.toLowerCase();
  const looksHtml = /^<!doctype|^<html|^<body|^</i.test(message);
  if (
    looksHtml ||
    lower.includes("json parse") ||
    lower.includes("unexpected token <") ||
    lower.includes("unexpected character: <") ||
    lower.includes("internal server error")
  ) {
    return resolvedFallback;
  }
  return message.slice(0, 240);
}

async function parseApiResponse(response) {
  const contentType = response.headers.get("content-type") || "";
  const text = await response.text().catch(() => "");
  let data = null;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = null;
    }
  }
  const looksHtml = contentType.includes("text/html") || /^<!doctype|^<html|^</i.test(text.trim());
  return { data, looksHtml };
}

const REFERRAL_SOURCE_OPTIONS = [
  { value: "", label: "Where did you hear about us? (optional)" },
  { value: "reddit", label: "Reddit" },
  { value: "twitter", label: "Twitter / X" },
  { value: "intervals_icu", label: "Intervals.icu" },
  { value: "zwift_garmin", label: "Zwift / Garmin groups" },
  { value: "friend", label: "Friend or word of mouth" },
  { value: "search_other", label: "Search or other" },
];

// Must match server (auth.js): 8+ chars, at least one uppercase, one lowercase, one number
const PASSWORD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/;

function Register() {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    confirmPassword: "",
    referralSource: "",
  });
  const [marketingEmailOptIn, setMarketingEmailOptIn] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const typedFieldGuardRef = useRef(null);
  if (typedFieldGuardRef.current === null) {
    typedFieldGuardRef.current = createRegisterTypedFieldGuard();
  }

  const navigate = useNavigate();

  const markTypedField = (fieldName) => {
    typedFieldGuardRef.current.markUserEdit(fieldName);
  };

  const handleUserEditKeyDown = (e) => {
    if (isRegisterUserEditKey(e.key)) {
      markTypedField(e.target.name);
    }
  };

  const handleUserEditGesture = (e) => {
    markTypedField(e.target.name);
  };

  const handleUserBeforeInput = (e) => {
    if (!isRegisterUserBeforeInputType(e.inputType)) return;
    markTypedField(e.target.name);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const next = applyRegisterProtectedChange(
        prev,
        name,
        value,
        typedFieldGuardRef.current,
      );
      if (next[name] !== value) {
        e.target.value = next[name] ?? "";
      }
      return next;
    });
    if (error) setError("");
  };

  const typedFieldInputProps = {
    onChange: handleChange,
    onKeyDown: handleUserEditKeyDown,
    onBeforeInput: handleUserBeforeInput,
    onPaste: handleUserEditGesture,
    onCompositionEnd: handleUserEditGesture,
  };

  const emailLooksValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email);
  const canSubmit = Boolean(
    formData.firstName.trim() &&
      formData.lastName.trim() &&
      emailLooksValid &&
      PASSWORD_REGEX.test(formData.password) &&
      formData.password === formData.confirmPassword
  );

  const handleEmailBlur = (e) => {
    const domValue = e.target.value;
    setFormData((prev) => {
      const next = syncEmailFromDom(prev.email, domValue);
      if (next === prev.email) return prev;
      return { ...prev, email: next };
    });
  };

  const validate = () => {
    if (
      !formData.firstName ||
      !formData.lastName ||
      !formData.email ||
      !formData.password ||
      !formData.confirmPassword
    ) {
      setError(t("auth.errors.fillAllFields"));
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError(t("auth.errors.emailInvalid"));
      return false;
    }

    if (formData.password !== formData.confirmPassword) {
      setError(t("auth.forgotPassword.passwordMismatch"));
      return false;
    }
    if (formData.password.length < 8) {
      setError(t("auth.errors.passwordMin8"));
      return false;
    }
    if (!PASSWORD_REGEX.test(formData.password)) {
      setError(t("auth.errors.passwordComplexity"));
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) return;

    if (turnstileSiteKey() && !turnstileToken) {
      setError("Confirm you are human, then try again.");
      return;
    }

    setIsLoading(true);
    setError("");
    setSuccess("");

    try {
      const response = await fetch(`${API_BASE}/api/auth/register`, {
        method: "POST",
        headers: { ...getAuthHeaders("POST"), "Content-Type": "application/json" },
        body: JSON.stringify({
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          password: formData.password,
          referralSource: formData.referralSource || undefined,
          marketingEmailOptIn: marketingEmailOptIn === true,
          turnstileToken: turnstileToken || undefined,
        }),
      });

      const { data, looksHtml } = await parseApiResponse(response);
      if (!data && looksHtml) {
        throw new Error(authUnavailableMessage());
      }
      if (!data) {
        throw new Error(authUnavailableMessage());
      }

      if (!response.ok) {
        const message =
          Array.isArray(data.errors) && data.errors.length > 0
            ? data.errors.map((e) => e.msg).join(". ")
            : data.message || t("common.errorGeneric");
        throw new Error(toSafeAuthMessage(message, t("common.errorGeneric")));
      }

      trackSignup(formData.referralSource || undefined);

      if (data.requiresVerification) {
        persistPendingEmailVerification(formData.email);
        setSuccess("check-email");
      } else {
        // Development mode - auto-login
        setSuccess(t("auth.errors.loginSuccess"));

        persistWebSession({
          token: data.token,
          refreshToken: data.refreshToken,
          user: data.user,
        });

        setTimeout(() => {
          navigate("/onboarding");
        }, 1000);
      }
    } catch (err) {
      console.error("Registration error:", err);
      setError(toSafeAuthMessage(err?.message));
    } finally {
      setIsLoading(false);
    }
  };

  if (success === "check-email") {
    return (
      <AuthInstrumentShell subtitle={t("auth.emailVerification.layoutSubtitle")}>
        <div
          className="instrument-fuel-tile w-full sm:p-6 lg:p-8"
          data-testid={REGISTER_CHECK_EMAIL_TESTID}
        >
          <div className="mb-5">
            <h1 className="font-display text-xl font-bold text-[var(--text-primary)]">
              {t("auth.emailVerification.titlePending")}
            </h1>
          </div>
          <div className="space-y-4" role="status">
            <p className="flex items-center gap-2 text-[var(--text-primary)] font-semibold">
              <CheckCircle
                className="w-4 h-4 text-success-600 dark:text-success-400 shrink-0"
                aria-hidden
              />
              {t("auth.emailVerification.sentLead")}{" "}
              <strong className="break-all">{formData.email}</strong>.
            </p>
            <p className="text-sm text-[var(--text-secondary)]">
              {t("auth.emailVerification.instructions")}
            </p>
            <Link
              to="/login"
              state={{
                [LOGIN_PENDING_VERIFICATION_STATE]: true,
                email: formData.email,
              }}
              className="inline-flex items-center justify-center min-h-[44px] btn-primary text-sm py-2 px-4"
            >
              {t("auth.emailVerification.backToLoginCta")}
            </Link>
          </div>
        </div>
      </AuthInstrumentShell>
    );
  }

  return (
    <AuthInstrumentShell>
        <div className="instrument-fuel-tile w-full sm:p-6 lg:p-8">
          <div className="mb-5">
            <h1 className="font-display text-xl font-bold text-[var(--text-primary)]">
              {t("auth.register.title")}
            </h1>
          </div>
          <form onSubmit={handleSubmit} className="space-y-4" aria-busy={isLoading}>
            <div
              className={REGISTER_NAME_GRID_CLASS}
              data-testid={REGISTER_NAME_GRID_TESTID}
            >
              <div className={REGISTER_NAME_FIELD_CLASS}>
                <label
                  htmlFor="firstName"
                  className="block text-sm font-medium text-stone-700 dark:text-stone-300 mb-1.5"
                >
                  {t("auth.register.firstNameLabel")}
                </label>
                <input
                  id="firstName"
                  name="firstName"
                  type="text"
                  required
                  autoComplete={REGISTER_FIELD_AUTOCOMPLETE.firstName}
                  value={formData.firstName}
                  {...typedFieldInputProps}
                  className={REGISTER_NAME_INPUT_CLASS}
                  placeholder={t("auth.register.firstNamePlaceholder")}
                />
              </div>

              <div className={REGISTER_NAME_FIELD_CLASS}>
                <label
                  htmlFor="lastName"
                  className="block text-sm font-medium text-stone-700 dark:text-stone-300 mb-1.5"
                >
                  {t("auth.register.lastNameLabel")}
                </label>
                <input
                  id="lastName"
                  name="lastName"
                  type="text"
                  required
                  autoComplete={REGISTER_FIELD_AUTOCOMPLETE.lastName}
                  value={formData.lastName}
                  {...typedFieldInputProps}
                  className={REGISTER_NAME_INPUT_CLASS}
                  placeholder={t("auth.register.lastNamePlaceholder")}
                />
              </div>
            </div>

            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-stone-700 dark:text-stone-300 mb-1.5"
              >
                {t("auth.register.emailLabel")}
              </label>
              <input
                id="email"
                name="email"
                type="email"
                required
                autoComplete={REGISTER_FIELD_AUTOCOMPLETE.email}
                value={formData.email}
                {...typedFieldInputProps}
                onBlur={handleEmailBlur}
                className="input-modern px-4 text-sm"
                placeholder={t("auth.register.emailPlaceholder")}
              />
            </div>

            <div>
              <label
                htmlFor="referralSource"
                className="block text-sm font-medium text-stone-700 dark:text-stone-300 mb-1.5"
              >
                Where did you hear about us?
              </label>
              <select
                id="referralSource"
                name="referralSource"
                autoComplete={REGISTER_FIELD_AUTOCOMPLETE.referralSource}
                value={formData.referralSource}
                onChange={handleChange}
                className="input-modern px-4 text-sm"
              >
                {REFERRAL_SOURCE_OPTIONS.map((opt) => (
                  <option key={opt.value || "empty"} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label
                htmlFor="password"
                className="block text-sm font-medium text-stone-700 dark:text-stone-300 mb-1.5"
              >
                {t("auth.register.passwordLabel")}
              </label>
              <div className="relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  required
                  autoComplete={REGISTER_FIELD_AUTOCOMPLETE.password}
                  value={formData.password}
                  {...typedFieldInputProps}
                  className={`input-modern pl-4 pr-20 text-sm ${AUTH_PASSWORD_SCROLL_MARGIN_CLASS}`}
                  placeholder={t("auth.register.passwordPlaceholder")}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center gap-1 text-xs font-semibold text-primary-600 dark:text-primary-400"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-pressed={showPassword}
                  aria-label={showPassword ? t("common.hide") : t("common.show")}
                >
                  {showPassword ? (
                    <EyeOff className="w-4 h-4 shrink-0" aria-hidden />
                  ) : (
                    <Eye className="w-4 h-4 shrink-0" aria-hidden />
                  )}
                  <span className="hidden sm:inline">
                    {showPassword ? t("common.hide") : t("common.show")}
                  </span>
                </button>
              </div>
            </div>

            <div>
              <label
                htmlFor="confirmPassword"
                className="block text-sm font-medium text-stone-700 dark:text-stone-300 mb-1.5"
              >
                {t("auth.forgotPassword.confirmPasswordLabel")}
              </label>
              <div className="relative">
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  required
                  autoComplete={REGISTER_FIELD_AUTOCOMPLETE.confirmPassword}
                  value={formData.confirmPassword}
                  {...typedFieldInputProps}
                  className={`input-modern pl-4 pr-20 text-sm ${AUTH_PASSWORD_SCROLL_MARGIN_CLASS}`}
                  placeholder={t("auth.forgotPassword.confirmPasswordPlaceholder")}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center gap-1 text-xs font-semibold text-primary-600 dark:text-primary-400"
                  onClick={() =>
                    setShowConfirmPassword(!showConfirmPassword)
                  }
                  aria-pressed={showConfirmPassword}
                  aria-label={showConfirmPassword ? t("common.hide") : t("common.show")}
                >
                  {showConfirmPassword ? (
                    <EyeOff className="w-4 h-4 shrink-0" aria-hidden />
                  ) : (
                    <Eye className="w-4 h-4 shrink-0" aria-hidden />
                  )}
                  <span className="hidden sm:inline">
                    {showConfirmPassword ? t("common.hide") : t("common.show")}
                  </span>
                </button>
              </div>
            </div>

            <label className="flex items-start gap-3 cursor-pointer min-h-[44px] py-1">
              <input
                id="marketingEmailOptIn"
                name="marketingEmailOptIn"
                type="checkbox"
                checked={marketingEmailOptIn}
                onChange={(e) => setMarketingEmailOptIn(e.target.checked)}
                className="mt-1 h-4 w-4 rounded border-stone-300 text-primary-600 focus:ring-primary-500"
              />
              <span className="text-sm text-stone-600 dark:text-stone-300">
                Send me product updates and launch news by email (optional). Account emails such as verification and password reset are separate.
              </span>
            </label>

            <TurnstileField onToken={setTurnstileToken} />

            <motion.button
              type="submit"
              disabled={isLoading || !canSubmit}
              aria-disabled={isLoading || !canSubmit}
              className="w-full btn-primary py-3 inline-flex items-center justify-center gap-2 disabled:opacity-50"
              whileHover={{ scale: 1.01 }}
              whileTap={{ scale: 0.99 }}
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin shrink-0" aria-hidden />
                  {t("auth.register.creatingAccount")}
                </>
              ) : (
                t("auth.register.submitButton")
              )}
            </motion.button>
          </form>

          <OAuthSocialButtons returnTo="/onboarding" disabled={isLoading} />

          {/* Error/Success Messages */}
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex items-start gap-2 p-3 bg-danger-50 dark:bg-danger-900/25 border border-danger-200 dark:border-danger-800 rounded-xl mt-4"
              role="alert"
            >
              <AlertCircle
                className="w-4 h-4 text-danger-600 dark:text-danger-400 shrink-0 mt-0.5"
                aria-hidden
              />
              <span className="text-danger-700 dark:text-danger-300 font-medium text-sm">
                {error}
              </span>
            </motion.div>
          )}

          {success && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex items-start gap-2 p-3 bg-success-50 dark:bg-success-900/20 border border-success-200 dark:border-success-800 rounded-xl mt-4"
              role="status"
            >
              <CheckCircle
                className="w-4 h-4 text-success-600 dark:text-success-400 shrink-0 mt-0.5"
                aria-hidden
              />
              <span className="text-success-700 dark:text-success-300 font-medium text-sm">
                {success}
              </span>
            </motion.div>
          )}

          {/* Login Link */}
          <div className="mt-4 pt-4 border-t border-stone-100 dark:border-stone-700 text-center">
            <p className="text-stone-500 dark:text-stone-400 text-sm">
              {t("auth.register.alreadyAccount")}{" "}
              <Link
                to="/login"
                className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-semibold transition-colors"
              >
                {t("auth.register.signIn")}
              </Link>
            </p>
          </div>
        </div>
    </AuthInstrumentShell>
  );
}

export default Register;
